<template>
	<div
		id="header-wrapper"
		class="header-wrapper"
		:class="{ 'header-wrapper--open': isOpen }"
	>
		<div
			class="header"
			:class="[
				{ 'pointer-events-none': isCameraActive },
				{
					'header--royal-provision': isRoyalProvisionActive,
				},
			]"
		>
			<div class="header-content">
				<div
					class="main-bar"
					:class="{ 'main-bar--event': isEventActive }"
				>
					<MainResource
						:currency="ResourceType.COINS"
						:value="resources.coins?.value ?? 0"
						:income="resources.coinsIncome?.value ?? 0"
					/>
					<MainResource
						:currency="ResourceType.FOOD_DIFF"
						:value="resources.foodDiff?.value ?? 0"
						:income="resources.foodIncome?.value ?? 0"
						:consumption="resources.foodConsumption?.value ?? 0"
					/>
					<MainResource
						:currency="ResourceType.POPULATION_DIFF"
						:value="resources.populationDiff?.value ?? 0"
						:income="resources.populationIncome?.value ?? 0"
						:consumption="
							resources.populationConsumption?.value ?? 0
						"
					/>
					<MainResource
						:currency="ResourceType.GEMS"
						:value="resources.gems?.value ?? 0"
					/>
					<MainResource
						v-if="isEventActive"
						class="main-resource--event"
						:currency="ResourceType.EVENTIUM"
						:income="resources.eventiumIncome?.value ?? 0"
						:value="resources.eventium?.value ?? 0"
						:total="resources.eventiumAccumulated?.value ?? 0"
					/>
				</div>
				<div class="sub-bar-wrapper" v-if="isOpen">
					<SubBar
						:resourcesUnlocks="resourcesUnlocks"
						:isFirstRowUnlocked="isFirstRowUnlocked"
						:isSecondRowUnlocked="isSecondRowUnlocked"
						:unlockLevels="unlockByLevel"
					/>
					<div
						class="dropdown-label-wrapper"
						v-if="isDropdownUnlocked"
					>
						<div
							class="dropdown-label"
							@click="subBarDropdownOpen = !subBarDropdownOpen"
						>
							<div
								class="toggle-arrow"
								:class="[
									subBarDropdownOpen
										? 'toggle-arrow--up arrow--up-26'
										: 'toggle-arrow--down arrow--down-26',
								]"
							/>
						</div>
					</div>
					<SubBarDropdown
						v-if="subBarDropdownOpen"
						:resourcesUnlocks="resourcesUnlocks"
						:unlockLevels="unlockByLevel"
					/>
				</div>
				<AppGameTickerProgressBar v-if="!tutorialActive" />
				<div
					v-if="isSomeSubResourceUnlocked"
					class="toggle"
					:class="{
						'toggle--royal-provision': isRoyalProvisionActive,
					}"
					@click="handleToggle"
				>
					<div
						class="toggle-arrow"
						:class="[
							isOpen
								? 'toggle-arrow--up arrow--up-26'
								: 'toggle-arrow--down arrow--down-26',
						]"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppGameTickerProgressBar from "@/components/AppGameTickerProgressBar.vue";
import SubBar from "@/components/Layout/SubBar.vue";
import MainResource from "@/components/MainResource.vue";
import { useEventStore } from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import SoundService from "@/game/SoundService";
import { getUnlocksByLevel } from "@/game/utils/resources";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUserStore from "@/store/useUserStore";
import { isMobile } from "@/utils/isMobile";
import { storeToRefs } from "pinia";
import { computed, markRaw, onMounted, ref, watch } from "vue";
import SubBarDropdown from "./SubBarDropdown.vue";

const soundService = markRaw(SoundService.getInstance());

const runtimeStore = useRuntimeStore();
const { isCameraActive } = storeToRefs(runtimeStore);

const userStore = useUserStore();
const { isRoyalProvisionActive } = storeToRefs(userStore);

const resourcesStore = useResourcesStore();
const resources = storeToRefs(resourcesStore);

const isOpen = ref<boolean>(false);

const settlementStore = useSettlementsStore();
const { mainLevel } = storeToRefs(settlementStore);

const unlockByLevel = getUnlocksByLevel();

const eventStore = useEventStore();
const { isActive: isEventActive } = storeToRefs(eventStore);

const questStore = useQuestStore();
const { tutorialActive } = storeToRefs(questStore);

const subBarDropdownOpen = ref<boolean>(false);

const handleToggle = (): void => {
	isOpen.value = !isOpen.value;
	setHeaderTotalHeight(); // Trigger header height update
	soundService.playGenerickClick();
};

// NOTE: very important computational part for left/right boxes & their top offset
// NOTE only THESE values are being revised and updated HERE, CSS reflects them via inheritance
const HeaderHeightOptions = computed(
	():
		| typeof HeaderMobileHeightOptions
		| typeof HeaderDesktopHeightOptions => {
		return isMobile()
			? HeaderMobileHeightOptions
			: HeaderDesktopHeightOptions;
	},
);

// adjusted progress bar offset (+0.25) applied globally
const HeaderMobileHeightOptions = {
	HEADER_UNOPEN_WITHOUT_NOTCH_ACTIVE: "4.25rem",
	HEADER_UNOPEN_WITH_NOTCH_ACTIVE: "4.25rem",
	HEADER_OPEN_ONE_SUB_ROW: "6rem",
	// added extra +0.188rem offset for the third row
	HEADER_OPEN_TWO_SUB_ROWS: "7.813rem",
} as const;

const HeaderDesktopHeightOptions = {
	HEADER_UNOPEN_WITHOUT_NOTCH_ACTIVE: "4.875rem",
	HEADER_UNOPEN_WITH_NOTCH_ACTIVE: "4.875rem",
	HEADER_OPEN_ONE_SUB_ROW: "6.625rem",
	// added extra +0.188rem offset for the third row
	HEADER_OPEN_TWO_SUB_ROWS: "8.313rem",
} as const;

const assignDynamicHeight = (newHeight: string): void => {
	document
		.getElementById("app")!
		.style.setProperty("--header-total-height", newHeight);
};

const resourcesUnlocks = computed<Partial<Record<ResourceType, boolean>>>(
	(): Partial<Record<ResourceType, boolean>> => {
		const result = {} as Partial<Record<ResourceType, boolean>>;
		(
			Object.entries(unlockByLevel) as Array<[ResourceType, number]>
		).forEach(([key, value]: [ResourceType, number]): void => {
			result[key] = value <= mainLevel.value;
		});
		return result;
	},
);

const isFirstRowUnlocked = computed<boolean>((): boolean => {
	return (
		Object.entries(resourcesUnlocks.value) as Array<[ResourceType, boolean]>
	).some(
		([key, unlock]: [ResourceType, boolean]): boolean =>
			unlock &&
			[
				ResourceType.LUMBER,
				ResourceType.STONE,
				ResourceType.CLAY,
			].includes(key),
	);
});

const isSecondRowUnlocked = computed<boolean>((): boolean => {
	return (
		Object.entries(resourcesUnlocks.value) as Array<[ResourceType, boolean]>
	).some(
		([key, unlock]: [ResourceType, boolean]): boolean =>
			unlock &&
			[
				ResourceType.IRON,
				ResourceType.GOODS,
				ResourceType.STEEL,
			].includes(key),
	);
});

const isDropdownUnlocked = computed<boolean>((): boolean => {
	return (
		Object.entries(resourcesUnlocks.value) as Array<[ResourceType, boolean]>
	).some(
		([key, unlock]: [ResourceType, boolean]): boolean =>
			unlock && [ResourceType.ARMY].includes(key),
	);
});

const isSomeSubResourceUnlocked = computed((): boolean => {
	return isFirstRowUnlocked.value || isSecondRowUnlocked.value;
});

const currentHeight = ref<string>("");

const setHeaderTotalHeight = (): void => {
	currentHeight.value = `calc(${
		isOpen.value
			? isFirstRowUnlocked.value && isSecondRowUnlocked.value
				? HeaderHeightOptions.value.HEADER_OPEN_TWO_SUB_ROWS
				: HeaderHeightOptions.value.HEADER_OPEN_ONE_SUB_ROW
			: isSomeSubResourceUnlocked.value
				? HeaderHeightOptions.value.HEADER_UNOPEN_WITH_NOTCH_ACTIVE
				: HeaderHeightOptions.value.HEADER_UNOPEN_WITHOUT_NOTCH_ACTIVE
	} + var(--safe-area-inset-top))`;
	assignDynamicHeight(currentHeight.value);
};

watch(
	isSomeSubResourceUnlocked,
	(): void => {
		// NOTE: Very important when adding a toggle for the notch by unlocking the first sub-resource (e.g. lumber)
		setHeaderTotalHeight(); // Trigger header height update
	},
	{ immediate: true },
);

watch(currentHeight, (newValue: string): void => {
	assignDynamicHeight(newValue);
});

watch(
	resourcesUnlocks,
	(): void => {
		// Automatically open the header when the first or second row is unlocked
		isOpen.value = isSomeSubResourceUnlocked.value ? true : false;

		setHeaderTotalHeight(); // Trigger header height update
	},
	{ immediate: true },
);

onMounted((): void => {
	setHeaderTotalHeight(); // Trigger initial header height update
});
</script>

<style lang="scss" scoped>
.header {
	display: flex;
	position: relative;
	top: 0;
	left: 0;
	flex-direction: column;
	justify-content: flex-end;
	align-items: center;
	background: transparent;
	padding: 0.125rem 0.25rem 0.375rem;
	width: 100%;
	pointer-events: auto;
	color: #fff;

	@include md {
		padding: 0.375rem 0.25rem;
	}

	& > * {
		z-index: 1;
	}

	&-wrapper {
		position: relative;
		top: 0;
		left: 0;
		width: 100%;
		height: var(
			--header-total-height
		); // dynamic CSS computed open/close value
		@include safe-area-top;
		z-index: 1;
		pointer-events: none;
	}

	&::before {
		position: absolute;
		bottom: 0;
		left: 50%;
		transform: translateX(-50%);
		z-index: 0;
		background: url($path-images + "backgrounds/textures/top-menu-texture-bg.avif")
			repeat center;
		background-size: 9.375rem auto;
		width: 100%;
		height: calc(10.85rem + var(--safe-area-inset-top));
		content: "";
	}

	&--royal-provision {
		&::before {
			background: url($path-images + "backgrounds/textures/top-menu-texture-bg--royal-provision.avif")
				repeat center;
			background-size: 9.375rem auto;
		}
	}

	&::after {
		position: absolute;
		bottom: 0;
		left: 0;
		z-index: 0;
		background: url($path-images + "backgrounds/textures/top-menu-texture-golden-stroke.avif")
			repeat center;
		width: 100%;
		height: 0.094rem;
		content: "";
	}

	&-content {
		max-width: 27rem;
	}

	.main-bar {
		display: flex;
		position: relative;
		justify-content: center;
		align-items: center;
		width: 100%;

		&--event {
			:deep(.main-resource-content__value) {
				font-size: 0.75rem;
			}
		}

		:deep(.main-resource--event) {
			.main-resource-bg .base {
				fill: url(#linear-gradient-event);
			}
		}
	}

	.progress-bar {
		margin-top: 0.188rem;
	}

	.toggle {
		display: flex;
		position: absolute;
		top: calc(100% - 0.54rem);
		left: 50%;
		justify-content: center;
		align-items: center;
		transform: translateX(-50%);
		transform-origin: top center;
		cursor: pointer;
		background: url($path-images + "backgrounds/top-menu-notch-faded.avif")
			no-repeat;
		background-size: contain;
		width: 7rem;
		height: 2.4752rem;

		&--royal-provision {
			background: url($path-images + "backgrounds/top-menu-notch-faded--royal-provision.avif")
				no-repeat;
			background-size: contain;
		}

		&-arrow {
			position: relative;

			&--down {
				top: 0;
			}

			&--up {
				top: -0.125rem;
			}
		}
	}
	.sub-bar-wrapper {
		position: relative;
		@include flex-row-center;
		.dropdown-label-wrapper {
			@include flex-row-center;
			border-top: 0.125rem solid #464550;
			border-right: 0.125rem solid #111015;
			border-bottom: 0.125rem solid #111015;
			border-left: 0.125rem solid #464550;
			background-color: #2e2d39;
			background: url($path-images + "backgrounds/textures/top-menu-texture-bg.avif")
				repeat center;
			width: 3.75rem;
			height: 3.75rem;
			.dropdown-label {
				box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
				border-width: 0.075rem;
				border-style: solid;
				border-image-source: linear-gradient(
					to top,
					rgb(189, 144, 50),
					rgb(240, 211, 134)
				);
				border-image-slice: 1;
				background-image: linear-gradient(
					to top,
					rgb(24, 26, 29),
					rgb(48, 50, 57)
				);
				width: 2.9375rem;
				height: 2.9375rem;
				@include flex-row-center;
				cursor: pointer;
			}
		}
	}
}

.header__title {
	font-weight: 700;
	font-size: 1.5rem;
}
</style>
